import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { fetchChildDetails, fetchReadiness, MyApiError, submitChildDetails, type ChildDetails, type ChildLane } from '../data/myApi'
import { bumpReadinessRefresh, childDetailsStored, profileBlockCopy } from '../data/getReady'
import { confirmGuardianChild, guardianClaimForTicket, recordGuardianClaim } from '../data/guardianClaim'
import { constrainYearsToEligibility, eligibilityGate, eligibilityMismatchCopy, type TicketEligibility } from '../data/eligibility'
import { EligibilityMismatchNotice } from '../components/EligibilityMismatchNotice'
import { useShellChrome } from '../components/Shell'
import { useLocale } from '../i18n/useLocale'
import { CHILD_YEARS, DAYS, dobIso, EMPTY_DRAFT, GENDER_MODES, MONTHS } from './wizard/wizardModel'

/*
 * CHILD-CONFIRM — the guardian-claimed seat's child-identity step (S2 FE
 * slice, BOARDROOM-2026-09-03-052111 / CMT-2026-09-03-020; ratified child-flow
 * rulings: the child is named by the CHILD everywhere; the parent signs FOR
 * the child).
 *
 * The PARENT is the authority here: the child's name arrives PRE-FILLED from
 * the claim wire's child_name (confirm/correct), and the DOB + gender are
 * COLLECTED here — the no-permission assign path captures neither.
 *
 * SERVER-BACKED (my-api V25, boardroom GO on #241): Confirm POSTs the facts to
 * /me/tickets/{id}/guardian-child, where the server validates the child
 * against the ticket's band + gender on race day and stores them — the age
 * gates then read THAT, and the seat cannot complete without it. The
 * device-local guardian-claim record (src/data/guardianClaim.ts) is now only a
 * cache: it is marked confirmed ONLY after the server accepted. A 422 shows
 * the server's band/gender refusal; a network/5xx failure shows a retry and
 * never marks the record confirmed. With no local record (another device —
 * the server routed the parent here via child_details_required) the form
 * still renders, empty, and the success seeds the cache.
 *
 * ELIGIBILITY runs against the CHILD's entered DOB/gender — never the
 * parent's — using the ticket's own readiness-carried contract (eligibility +
 * raceDay) and the existing copy patterns (eligibilityMismatchCopy +
 * EligibilityMismatchNotice with the support out). ADVISORY posture, like the
 * wizard's fail-fast: the parent can still save (the server's per-seat gate
 * stays the net); a missing contract axis never fires (allow-on-unknown).
 *
 * SERVER-BACKED FOR BOTH LANES (boardroom 2026-10-01, quality F1/F2): the
 * screen is keyed by TICKET (route /get-ready/child-details/:ticketId) and
 * serves the guardian-claimed seat AND the kind='child' seat (assigned to the
 * child; identity captured at assign). On mount it LOADS the stored identity
 * via GET /me/tickets/{id}/child-details — the server's values outrank the
 * device-local cache, which is only the fallback seed (and the purchaser-typed
 * childName the prefill of a not-yet-confirmed guardian seat). Save POSTs
 * /me/tickets/{id}/child-details (same validation as V25). A kind='child'
 * seat keeps its last name REQUIRED (V15 CHECK), and its save never touches
 * the guardian-claim cache (a guardian record there would flip the card to
 * guardian treatment).
 *
 * Presentation: the SHARED flow chrome (HeatSelect pattern — header with the
 * back chevron + event name; scrolling body; the amber blocked-reason bar
 * directly above the footer CTA). Reuses the wizard's child identity
 * primitives: DAYS/MONTHS/CHILD_YEARS explicit DOB selects (never one
 * ambiguous DD/MM field) + the binary gender pills.
 */

const inputStyle: CSSProperties = {
  width: '100%', minHeight: 46, border: '1px solid var(--hairline-14)',
  borderRadius: 11, padding: '0 13px', fontSize: 15, background: 'var(--bg-frame)',
  color: 'var(--text)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
}
const selectStyle: CSSProperties = {
  ...inputStyle, padding: '0 8px', appearance: 'none', WebkitAppearance: 'none',
}
const labelStyle: CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6,
}
const pillStyle = (selected: boolean): CSSProperties => ({
  borderRadius: 20, padding: '0 15px', minHeight: 44, fontFamily: 'inherit',
  fontSize: 13, fontWeight: 700, cursor: 'pointer',
  ...(selected
    ? { background: 'var(--accent)', color: 'var(--accent-ink)', border: 0 }
    : { background: 'var(--bg-frame)', color: 'var(--text-softbtn)', border: '1px solid var(--hairline-14)' }),
})
const eyebrowStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.1em',
  textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 4,
}
const backBtnStyle: CSSProperties = {
  flex: 'none', border: '1px solid var(--hairline-14)', background: 'transparent',
  color: 'var(--text-softbtn)', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
  cursor: 'pointer', minHeight: 48, borderRadius: 12, padding: '0 16px',
}
const ctaStyle = (ok: boolean): CSSProperties => ({
  flex: 1, border: 0, borderRadius: 12, minHeight: 48, background: 'var(--accent)',
  color: 'var(--accent-ink)', fontSize: 15, fontWeight: 800, fontFamily: 'inherit',
  ...(ok ? { cursor: 'pointer' } : { opacity: 0.35, cursor: 'not-allowed' }),
})

function Gap({ h }: { h: number }) {
  return <div style={{ height: h }} />
}

/** Server DOB 'YYYY-MM-DD' → the wizard's day / month-NAME / year selects. */
function dobParts(iso: string | null | undefined): { d: string; m: string; y: string } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '')
  if (!m) return null
  const month = MONTHS[Number(m[2]) - 1]
  return month ? { d: String(Number(m[3])), m: month, y: m[1] } : null
}

/** Server gender 'M'/'F' → the binary pill value; anything else → ''. */
function genderPill(g: ChildDetails['gender'] | undefined): string {
  return g === 'F' ? 'Female' : g === 'M' ? 'Male' : ''
}

export function ChildConfirm({
  eventName,
  idToken,
  base,
  ticketId,
  eventId,
  onBack,
  onDone,
}: {
  eventName: string
  idToken: string
  base: string
  ticketId: string
  /** The seat's event — seeds the local cache record when this device had
   *  none (cross-device parent). Optional: absent ⇒ no cache is created. */
  eventId?: string
  onBack: () => void
  onDone: () => void
}) {
  const { isMobile } = useShellChrome()
  const { t, dir } = useLocale()
  const backChev = dir === 'rtl' ? '›' : '‹'

  // Seed from the device-local guardian-claim record (child name pre-filled
  // from the claim wire's child_name; DOB/gender from an earlier confirm when
  // re-editing). No record ⇒ an empty form: it saves to the SERVER (V25), so
  // it is never a form that saves nowhere.
  const [rec] = useState(() => guardianClaimForTicket(ticketId))
  // Two fields (ruling 5 on #241): First name + OPTIONAL Last name. Pre-fill
  // from the parent's own earlier split when the cache has one; otherwise the
  // WHOLE child_name seed goes into First name — never a guessed split.
  const [first, setFirst] = useState(rec?.childFirst ?? rec?.childName ?? '')
  const [last, setLast] = useState(rec?.childLast ?? '')
  const name = [first.trim(), last.trim()].filter(Boolean).join(' ')
  const [dobD, setDobD] = useState(rec?.dobD ?? '')
  const [dobM, setDobM] = useState(rec?.dobM ?? '')
  const [dobY, setDobY] = useState(rec?.dobY ?? '')
  const [gender, setGender] = useState(rec?.gender ?? '')
  // The seat's LANE off the server read (null until known / read failed).
  const [lane, setLane] = useState<ChildLane | null>(null)
  // The server-seeded birth year rides along in the year offer like the
  // local record's (never silently lost from a narrowed list).
  const [seedY, setSeedY] = useState('')
  // Once the parent edits any field, a late server read never overwrites it.
  const touched = useRef(false)
  const edit = <T,>(set: (v: T) => void) => (v: T) => { touched.current = true; set(v) }

  // SERVER LOAD (GET child-details): the stored identity seeds the form —
  // server values outrank the local cache; an absent value leaves the local
  // seed. A failed read keeps the local seed (the save is still the server's).
  useEffect(() => {
    let cancelled = false
    fetchChildDetails(idToken, base, ticketId)
      .then((d) => {
        if (cancelled) return
        if (d.kind === 'child' || d.kind === 'guardian') setLane(d.kind)
        if (touched.current) return
        if (d.firstName) {
          setFirst(d.firstName)
          setLast(d.lastName ?? '')
        } else if (!rec && d.childName) {
          // Not-yet-confirmed guardian seat on a device with no cache: the
          // purchaser-typed name is the WHOLE first-name seed (never a split).
          setFirst(d.childName)
        }
        const parts = dobParts(d.dob)
        if (parts) {
          setDobD(parts.d)
          setDobM(parts.m)
          setDobY(parts.y)
          setSeedY(parts.y)
        }
        const g = genderPill(d.gender)
        if (g) setGender(g)
      })
      .catch(() => { /* no server read — the local seed stands; save still goes to the server */ })
    return () => { cancelled = true }
  }, [idToken, base, ticketId, rec])
  const lastRequired = lane === 'child'

  // The ticket's readiness-carried eligibility contract + race-day anchor
  // (BOARDROOM-2026-08-28-194055 item 4 carry). BEST-EFFORT: a failed read /
  // absent contract simply skips the FE check (allow-on-unknown) — the server
  // net still enforces; nothing is guessed.
  const [contract, setContract] = useState<{ eligibility: TicketEligibility | null; raceDay: Date | null }>(
    { eligibility: null, raceDay: null },
  )
  useEffect(() => {
    let cancelled = false
    fetchReadiness(idToken, base, ticketId)
      .then((r) => {
        if (cancelled) return
        const anchor = r.raceDay ?? r.eventStartsAt ?? null
        const raceDay = anchor ? new Date(anchor) : null
        setContract({
          eligibility: r.eligibility ?? null,
          raceDay: raceDay && !Number.isNaN(raceDay.getTime()) ? raceDay : null,
        })
      })
      .catch(() => { /* no contract in hand — the server net enforces */ })
    return () => { cancelled = true }
  }, [idToken, base, ticketId])

  // FAIL-FAST vs the CHILD's entered facts (never the parent's) — derived per
  // render, the wizard's inline-mismatch posture: fires on the selection that
  // creates the mismatch, clears on the one that resolves it, never blocks.
  const childDobIso = dobIso({ ...EMPTY_DRAFT, dobD, dobM, dobY })
  const verdict = eligibilityGate({ gender, birthdate: childDobIso }, contract.eligibility, contract.raceDay)
  const mismatch = !verdict.ok
    ? { kind: verdict.kind, copy: eligibilityMismatchCopy(verdict.kind, contract.eligibility) }
    : null

  // CONSTRAIN-THEN-VALIDATE (BOARDROOM-2026-09-05-101330): the year dropdown
  // OFFERS only years where the child could be inside the ticket's age band on
  // race day (boundary years inclusive — the full DOB decides), instead of 21
  // impossible choices that only complain after selection. The eligibilityGate
  // above STAYS the final arbiter; contract/anchor absent ⇒ the untouched
  // CHILD_YEARS fallback (never a fabricated range — PROCESS-3).
  // - EMPTY narrow set ⇒ the contract is unusable against CHILD_YEARS (e.g. an
  //   adult band on a child screen): full-list fallback — advisory-never-
  //   blocks is the invariant, and an empty offer would hard-block the DOB.
  // - ESCAPE (progressive disclosure): the narrowed offer stays the default,
  //   but a text-button below the select expands to the full list for the
  //   rest of this screen's session — the honest path for a fresh out-of-band
  //   entry (the true DOB is always enterable; the mismatch advisory +
  //   support out then fire).
  // - RIDE-ALONG: a SEEDED record year (rec.dobY — the stable fact, not the
  //   live selection) outside the narrowed range must NOT be silently lost;
  //   it rides along in descending position so it stays selected AND
  //   re-selectable after a mis-tap. The live dobY rides too (belt-and-
  //   braces with the escape) so the current selection is never orphaned.
  const [showAllYears, setShowAllYears] = useState(false)
  const narrowedYears = constrainYearsToEligibility(CHILD_YEARS, contract.eligibility, contract.raceDay)
  const offerNarrowed = !showAllYears && narrowedYears.length > 0 && narrowedYears.length < CHILD_YEARS.length
  const baseYears = offerNarrowed ? narrowedYears : CHILD_YEARS
  const rideAlongs = [...new Set([rec?.dobY, seedY, dobY])].filter(
    (y): y is string => !!y && !baseYears.includes(y),
  )
  const yearOptions = rideAlongs.length
    ? [...baseYears, ...rideAlongs].sort((a, b) => Number(b) - Number(a))
    : baseYears

  // First name is required; the last name is optional (single-word names are
  // legitimate — ruling 5). Whitespace runs collapse (the server's hygiene).
  const firstName = first.trim().replace(/\s+/g, ' ')
  const lastName = last.trim().replace(/\s+/g, ' ')
  const needs: string[] = []
  if (!firstName) needs.push(t('childConfirm.block.first'))
  if (lastRequired && !lastName) needs.push(t('childConfirm.block.last'))
  if (!dobD || !dobM || !dobY) needs.push(t('childConfirm.block.dob'))
  if (!gender) needs.push(t('childConfirm.block.gender'))
  const [phase, setPhase] = useState<
    | { kind: 'idle' }
    | { kind: 'saving' }
    | { kind: 'band'; copy: string }
    | { kind: 'retry' }
  >({ kind: 'idle' })
  const ok = needs.length === 0 && phase.kind !== 'saving'

  const confirm = async () => {
    if (!ok) return
    const serverGender = gender === 'Female' ? 'F' : gender === 'Male' ? 'M' : null
    if (!serverGender || !childDobIso) return
    setPhase({ kind: 'saving' })
    try {
      await submitChildDetails(idToken, base, ticketId, {
        firstName,
        ...(lastName ? { lastName } : {}),
        dob: childDobIso,
        gender: serverGender,
      })
    } catch (e) {
      // 422 = the SERVER's verdict on THIS child (band/gender on race day, or
      // an unverifiable age): show it — locale-mapped via the machine reason +
      // the in-hand contract, else the server's own copy. Anything else
      // (network, 5xx, …) is a retry: the details were NOT stored, so the
      // record is NEVER marked confirmed.
      if (e instanceof MyApiError && e.status === 422) {
        const reason = e.reason === 'gender' || e.reason === 'age' ? e.reason : null
        setPhase({ kind: 'band', copy: profileBlockCopy({ reason, message: e.serverMessage ?? null }, contract.eligibility, t) })
      } else {
        setPhase({ kind: 'retry' })
      }
      return
    }
    // Stored server-side → on a GUARDIAN seat the local record is the cache
    // of it (seeded here when this device had none — only when the server
    // SAID guardian: a kind='child' seat must never gain a guardian record).
    if (lane !== 'child') {
      if (!rec && eventId && lane === 'guardian') recordGuardianClaim({ ticketId, eventId, childName: name })
      confirmGuardianChild(ticketId, { childName: name, childFirst: firstName, childLast: lastName, dobD, dobM, dobY, gender })
    }
    // The profile-complete latch can pass now — let the next Get-ready load
    // re-attempt it; the card re-derives (childConfirm → done from the
    // server's childDetailsComplete) on return.
    childDetailsStored(ticketId)
    bumpReadinessRefresh()
    onDone()
  }

  // --- flow chrome geometry (the shared flow frame — HeatSelect pattern) ---
  const pad = isMobile
    ? { margin: '-18px -16px -26px', height: 'calc(100% + 44px)' }
    : { margin: '-26px -30px -36px', height: 'calc(100% + 62px)' }
  const headPad = isMobile ? '12px 16px 10px' : '18px 26px 12px'
  const bodyPad = isMobile ? '16px 16px 20px' : '20px 26px 24px'
  const footPad = isMobile ? '10px 16px 12px' : '14px 26px 16px'
  const blockPadX = isMobile ? 16 : 26

  const heading = name.trim()
    ? t('childConfirm.heading', { name: name.trim() })
    : t('childConfirm.headingNoName')

  return (
    <div id="childconfirm.root" style={{ ...pad, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* HEADER — back chevron + event context */}
      <div data-testid="childconfirm-head" style={{ flex: 'none', padding: headPad, borderBottom: '1px solid var(--hairline-07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <button
            data-testid="childconfirm-back"
            onClick={onBack}
            aria-label={t('flow.close')}
            style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: 0 }}
          >{backChev}</button>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{eventName}</span>
        </div>
      </div>

      {/* BODY */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: bodyPad }}>
        {/* No local record (another device) no longer dead-ends: the form
            saves to the SERVER (V25), so it renders empty and the parent
            enters the facts — never fabricated. */}
        <>
            <div style={eyebrowStyle}>{t('childConfirm.eyebrow')}</div>
            <h1 data-testid="childconfirm-heading" style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 6px' }}>
              {heading}
            </h1>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 20px' }}>
              {t('childConfirm.intro')}
            </p>

            <label htmlFor="childconfirm-first" style={labelStyle}>{t('childConfirm.field.first')}</label>
            <input
              id="childconfirm-first"
              data-testid="childconfirm-first"
              value={first}
              onChange={(e) => edit(setFirst)(e.target.value)}
              style={inputStyle}
            />
            <Gap h={14} />
            <label htmlFor="childconfirm-last" style={labelStyle}>{t(lastRequired ? 'childConfirm.field.lastRequired' : 'childConfirm.field.last')}</label>
            <input
              id="childconfirm-last"
              data-testid="childconfirm-last"
              value={last}
              onChange={(e) => edit(setLast)(e.target.value)}
              style={inputStyle}
            />
            <Gap h={20} />

            <label style={labelStyle}>{t('assign.field.dob')}</label>
            {/* Three explicit selects — DOB is NEVER one ambiguous DD/MM field
                (the wizard/assign pattern, reused verbatim). */}
            <div style={{ display: 'grid', gridTemplateColumns: '58px max-content 72px', gap: 8, justifyContent: 'start' }}>
              <select data-testid="childconfirm-dob-day" aria-label={t('assign.field.dob.day')} value={dobD || t('assign.field.dob.day')} onChange={(e) => edit(setDobD)(e.target.value === t('assign.field.dob.day') ? '' : e.target.value)} style={selectStyle}>
                {[t('assign.field.dob.day'), ...DAYS].map((d) => <option key={d}>{d}</option>)}
              </select>
              <select data-testid="childconfirm-dob-month" aria-label={t('assign.field.dob.month')} value={dobM || t('assign.field.dob.month')} onChange={(e) => edit(setDobM)(e.target.value === t('assign.field.dob.month') ? '' : e.target.value)} style={selectStyle}>
                {[t('assign.field.dob.month'), ...MONTHS].map((m) => <option key={m}>{m}</option>)}
              </select>
              <select data-testid="childconfirm-dob-year" aria-label={t('assign.field.dob.year')} value={dobY || t('assign.field.dob.year')} onChange={(e) => edit(setDobY)(e.target.value === t('assign.field.dob.year') ? '' : e.target.value)} style={selectStyle}>
                {[t('assign.field.dob.year'), ...yearOptions].map((y) => <option key={y}>{y}</option>)}
              </select>
            </div>

            {/* ESCAPE from the narrowed year offer (progressive disclosure):
                the honest path for a fresh out-of-band entry — expands to the
                full CHILD_YEARS for the rest of this screen's session. Quiet
                tertiary text-button (the EligibilityMismatchNotice reopen-link
                pattern), shown only while the offer is actually narrowed. */}
            {offerNarrowed && (
              <button
                data-testid="childconfirm-all-years"
                onClick={() => setShowAllYears(true)}
                style={{
                  display: 'block', border: 0, background: 'transparent', padding: 0,
                  marginTop: 8, minHeight: 24, fontFamily: 'inherit', fontSize: 11,
                  fontWeight: 600, color: 'var(--tru-text-tertiary, var(--text-secondary))',
                  textDecoration: 'underline', cursor: 'pointer', textAlign: 'start',
                }}
              >
                {t('childConfirm.dob.otherYear')}
              </button>
            )}

            {/* AGE-AXIS fail-fast vs the CHILD's DOB — the ticket's real band
                copy + the support out (existing eligibility copy patterns). */}
            {mismatch?.kind === 'age' && (
              <div data-testid="childconfirm-mismatch" data-kind="age" style={{ marginTop: 12 }}>
                <EligibilityMismatchNotice
                  key={ticketId}
                  ticketId={ticketId}
                  eventId={eventId}
                  eventName={eventName}
                  copy={mismatch.copy ? t(mismatch.copy.key, mismatch.copy.params) : verdict.ok ? '' : verdict.message}
                  reason="age"
                  participantName={name.trim() || undefined}
                />
              </div>
            )}

            <Gap h={20} />
            <label style={labelStyle}>{t('wizard.field.gender')}</label>
            <Gap h={3} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {GENDER_MODES.binary.opts.map((g) => (
                <button key={g} data-testid="childconfirm-gender-pill" data-value={g} onClick={() => edit(setGender)(g)} style={pillStyle(gender === g)}>{g}</button>
              ))}
            </div>

            {/* GENDER-AXIS fail-fast vs the CHILD's gender. */}
            {mismatch?.kind === 'gender' && (
              <div data-testid="childconfirm-mismatch" data-kind="gender" style={{ marginTop: 12 }}>
                <EligibilityMismatchNotice
                  key={ticketId}
                  ticketId={ticketId}
                  eventId={eventId}
                  eventName={eventName}
                  copy={mismatch.copy ? t(mismatch.copy.key, mismatch.copy.params) : verdict.ok ? '' : verdict.message}
                  reason="gender"
                  participantName={name.trim() || undefined}
                />
              </div>
            )}
        </>
      </div>

      {/* SERVER REFUSAL / RETRY (V25 save) — above the blocked bar. A 422 is
          the server's verdict on this child (band/gender); anything else is a
          retry — the details were NOT stored, nothing is marked confirmed. */}
      {(phase.kind === 'band' || phase.kind === 'retry') && (
        <div
          data-testid="childconfirm-server-error"
          data-kind={phase.kind}
          role="alert"
          style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: `10px ${blockPadX}px`, background: 'rgba(230,90,90,.08)', borderTop: '1px solid rgba(230,90,90,.28)' }}
        >
          <span aria-hidden style={{ flex: 'none', color: 'var(--warn)', fontSize: 13 }}>⚠</span>
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--warn)', fontWeight: 700 }}>
            {phase.kind === 'band' ? phase.copy : t('childConfirm.error.retry')}
          </span>
        </div>
      )}

      {/* BLOCKED-REASON BAR — directly above the footer (the shared pattern). */}
      {needs.length > 0 && (
        <div data-testid="childconfirm-block" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: `10px ${blockPadX}px`, background: 'rgba(230,193,90,.1)', borderTop: '1px solid rgba(230,193,90,.28)' }}>
          <span aria-hidden style={{ flex: 'none', color: 'var(--warn)', fontSize: 13 }}>⚠</span>
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--warn)', fontWeight: 700 }}>{needs[0]}</span>
        </div>
      )}

      {/* FOOTER */}
      <div style={{ flex: 'none', display: 'flex', gap: 10, padding: footPad, borderTop: '1px solid var(--hairline-07)' }}>
        <button data-testid="childconfirm-back-btn" onClick={onBack} style={backBtnStyle}>{t('flow.back')}</button>
        <button data-testid="childconfirm-cta" onClick={() => { void confirm() }} disabled={!ok} style={ctaStyle(ok)}>
          {phase.kind === 'saving'
            ? t('childConfirm.saving')
            : phase.kind === 'retry'
              ? t('childConfirm.cta.retry')
              : t('childConfirm.cta')}
        </button>
      </div>
    </div>
  )
}
